<template>
  <b-card no-body class="news-processing-status">
    <b-card-header class="d-flex justify-content-between align-items-center pb-50">
      <h5 class="mb-0">
        <feather-icon icon="ActivityIcon" size="16" class="mr-50 text-primary" />
        Rastreamento de Processamento
      </h5>
      <div class="d-flex align-items-center">
        <b-badge
          v-if="hasErrors"
          variant="light-danger"
          class="mr-75"
        >
          {{ errorCount }} erro{{ errorCount > 1 ? 's' : '' }}
        </b-badge>
        <b-badge
          v-if="hasRetries"
          variant="light-warning"
          class="mr-75"
        >
          {{ retryCount }} em retry
        </b-badge>
        <b-button
          variant="flat-secondary"
          size="sm"
          class="btn-icon"
          :disabled="loading"
          @click="loadStatus"
        >
          <feather-icon :icon="loading ? 'LoaderIcon' : 'RefreshCwIcon'" size="14" :class="{ 'spin-icon': loading }" />
        </b-button>
      </div>
    </b-card-header>

    <b-card-body class="p-0">
      <!-- Loading -->
      <div v-if="loading && !statusList.length" class="text-center py-2">
        <b-spinner small variant="primary" class="mr-50" />
        <span class="text-muted small">Carregando...</span>
      </div>

      <!-- Empty -->
      <div v-else-if="!loading && !statusList.length" class="text-center py-2">
        <feather-icon icon="CheckCircleIcon" size="20" class="text-muted mb-50" />
        <p class="text-muted small mb-0">Nenhum registro de processamento</p>
      </div>

      <!-- Table -->
      <b-table
        v-else
        :items="statusList"
        :fields="fields"
        small
        responsive
        borderless
        class="mb-0 processing-status-table"
      >
        <!-- Etapa -->
        <template #cell(process)="data">
          <span class="small font-weight-bold text-truncate d-block" style="max-width: 180px;" :title="data.item.process">
            {{ formatProcess(data.item.process) }}
          </span>
          <!-- <small v-if="data.item.targetId" class="text-muted">{{ data.item.targetId }}</small> -->
        </template>

        <!-- Status badge -->
        <template #cell(status)="data">
          <b-badge :variant="statusVariant(data.item.status)" class="text-capitalize">
            <feather-icon :icon="statusIcon(data.item.status)" size="10" class="mr-25" />
            {{ data.item.status }}
          </b-badge>
        </template>

        <!-- Tentativas -->
        <template #cell(retryCount)="data">
          <span class="small">
            {{ data.item.retryCount }}/{{ data.item.maxRetry }}
          </span>
        </template>

        <!-- Última tentativa -->
        <template #cell(lastAttemptAt)="data">
          <span class="small text-muted" :title="formatDateFull(data.item.lastAttemptAt)">
            {{ formatDateShort(data.item.lastAttemptAt) }}
          </span>
        </template>

        <!-- Próximo retry -->
        <template #cell(nextRetryAt)="data">
          <span v-if="data.item.nextRetryAt" class="small text-muted" :title="formatDateFull(data.item.nextRetryAt)">
            {{ formatDateShort(data.item.nextRetryAt) }}
          </span>
          <span v-else class="text-muted small">—</span>
        </template>

        <!-- Erro -->
        <template #cell(lastError)="data">
          <span
            v-if="data.item.lastError"
            class="small text-danger text-truncate d-block cursor-pointer"
            style="max-width: 140px;"
            :title="data.item.lastError"
            @click="showError(data.item)"
          >
            {{ data.item.lastError }}
          </span>
          <span v-else class="text-muted small">—</span>
        </template>

        <!-- Ações -->
        <template #cell(actions)="data">
          <b-button
            v-if="data.item.status === 'Error' || data.item.status === 'Retry'"
            variant="flat-warning"
            size="sm"
            class="btn-icon p-25"
            :disabled="retrying === getKey(data.item)"
            :title="'Forçar retry de ' + formatProcess(data.item.process)"
            @click="forceRetry(data.item)"
          >
            <feather-icon
              :icon="retrying === getKey(data.item) ? 'LoaderIcon' : 'RotateCcwIcon'"
              size="13"
              :class="{ 'spin-icon': retrying === getKey(data.item) }"
            />
          </b-button>
          <b-button
            v-if="data.item.status === 'Success'"
            variant="flat-success"
            size="sm"
            class="btn-icon p-25"
            disabled
            title="Sucesso"
          >
            <feather-icon icon="CheckIcon" size="13" />
          </b-button>
        </template>
      </b-table>
    </b-card-body>

    <!-- Modal de detalhe do erro -->
    <b-modal
      ref="errorDetailModal"
      title="Detalhe do Erro"
      ok-only
      ok-title="Fechar"
      ok-variant="secondary"
      centered
      size="md"
    >
      <div v-if="selectedError">
        <div class="mb-1">
          <strong>Etapa:</strong>
          <b-badge variant="light-secondary" class="ml-50">{{ formatProcess(selectedError.process) }}</b-badge>
        </div>
        <div class="mb-1">
          <strong>Status:</strong>
          <b-badge :variant="statusVariant(selectedError.status)" class="ml-50">{{ selectedError.status }}</b-badge>
        </div>
        <div class="mb-1">
          <strong>Tentativas:</strong> {{ selectedError.retryCount }}/{{ selectedError.maxRetry }}
        </div>
        <div v-if="selectedError.lastAttemptAt" class="mb-1">
          <strong>Última tentativa:</strong> {{ formatDateFull(selectedError.lastAttemptAt) }}
        </div>
        <div v-if="selectedError.nextRetryAt" class="mb-1">
          <strong>Próximo retry:</strong> {{ formatDateFull(selectedError.nextRetryAt) }}
        </div>
        <b-card bg-variant="dark" class="mt-1 mb-0">
          <pre class="text-danger mb-0 small" style="white-space: pre-wrap; word-break: break-all;">{{ selectedError.lastError }}</pre>
        </b-card>
      </div>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BCard, BCardHeader, BCardBody,
  BBadge, BButton, BTable,
  BSpinner, BModal,
} from 'bootstrap-vue';
import newsService from '@/providers/newsService';
import { formatDate } from '@core/utils/filter';

export default {
  name: 'ManageNewsProcessingStatus',
  components: {
    BCard, BCardHeader, BCardBody,
    BBadge, BButton, BTable,
    BSpinner, BModal,
  },
  props: {
    newsId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      retrying: null,
      statusList: [],
      selectedError: null,
      fields: [
        { key: 'process',       label: 'Etapa',            thStyle: 'min-width:140px' },
        { key: 'status',        label: 'Status',           thStyle: 'width:110px' },
        { key: 'retryCount',    label: 'Tentativas',       thStyle: 'width:90px' },
        { key: 'lastAttemptAt', label: 'Última Tentativa', thStyle: 'width:130px' },
        { key: 'nextRetryAt',   label: 'Próximo Retry',    thStyle: 'width:120px' },
        { key: 'lastError',     label: 'Último Erro',      thStyle: 'min-width:120px' },
        { key: 'actions',       label: '',                 thStyle: 'width:44px' },
      ],
    };
  },
  computed: {
    hasErrors() {
      return this.statusList.some(s => s.status === 'Error');
    },
    hasRetries() {
      return this.statusList.some(s => s.status === 'Retry');
    },
    errorCount() {
      return this.statusList.filter(s => s.status === 'Error').length;
    },
    retryCount() {
      return this.statusList.filter(s => s.status === 'Retry').length;
    },
  },
  watch: {
    newsId(val) {
      if (val) this.loadStatus();
      else this.statusList = [];
    },
  },
  mounted() {
    if (this.newsId) this.loadStatus();
  },
  methods: {
    async loadStatus() {
      if (!this.newsId) return;
      this.loading = true;
      try {
        const res = await newsService.getProcessingStatus(this.newsId);
        console.log('[loadStatus]', res);
        this.statusList = res || [];
      } catch (e) {
        console.log('[loadStatus] Erro ao carregar status de processamento', e);
        // this.$bvToast.toast('Erro ao carregar status de processamento', {
        //   title: 'Processamento',
        //   variant: 'danger',
        //   solid: true,
        // });
      } finally {
        this.loading = false;
      }
    },

    async forceRetry(item) {
      const key = this.getKey(item);
      this.retrying = key;
      try {
        await newsService.forceRetryProcessingStatus(this.newsId, {
          process: item.process,
          targetId: item.targetId || '',
        });
        this.$bvToast.toast(`Retry agendado para "${this.formatProcess(item.process)}"`, {
          title: 'Processamento',
          variant: 'success',
          solid: true,
        });
        await this.loadStatus();
      } catch (e) {
        this.$bvToast.toast('Erro ao solicitar retry', {
          title: 'Processamento',
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.retrying = null;
      }
    },

    showError(item) {
      this.selectedError = item;
      this.$refs.errorDetailModal.show();
    },

    getKey(item) {
      return `${item.process}__${item.targetId || ''}`;
    },

    formatProcess(process) {
      if (!process) return '—';
      return process
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, c => c.toUpperCase());
    },

    statusVariant(status) {
      const map = {
        Success: 'light-success',
        Retry:   'light-warning',
        Error:   'light-danger',
        Pending: 'light-secondary',
      };
      return map[status] || 'light-secondary';
    },

    statusIcon(status) {
      const map = {
        Success: 'CheckCircleIcon',
        Retry:   'RefreshCwIcon',
        Error:   'AlertCircleIcon',
        Pending: 'ClockIcon',
      };
      return map[status] || 'CircleIcon';
    },

    formatDateShort(date) {
      if (!date) return '—';
      return formatDate(date, { dateStyle: 'short', timeStyle: 'short' });
    },

    formatDateFull(date) {
      if (!date) return '—';
      return formatDate(date, { dateStyle: 'medium', timeStyle: 'medium' });
    },
  },
};
</script>

<style scoped>
.news-processing-status {
  margin-top: 1rem;
}
.processing-status-table >>> th {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #b9b9c3;
  border-top: none;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}
.processing-status-table >>> td {
  vertical-align: middle;
  padding-top: 0.4rem;
  padding-bottom: 0.4rem;
}
.spin-icon {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
.cursor-pointer {
  cursor: pointer;
}
</style>
